/**
 * Frontend Authentication Service
 * 
 * CRITICAL: This service ONLY communicates with backend APIs
 * NO DIRECT POCKETBASE INTERACTION ALLOWED FROM FRONTEND
 * 
 * NOW USES HTTP-ONLY SESSION COOKIES - NO LOCALSTORAGE
 */

import { debug } from './debug';

export interface User {
    id: string;
    email: string;
    name?: string;
    tier: number;
    subscription_title?: string; // e.g., "Basic", "Level 1", "Level 2"
    verified: boolean;
    avatar?: string;
    subscription_status?: string;
    subscription_start?: string;
    subscription_end?: string;
    api_token?: string;
    created?: string;
    updated?: string;
}

export interface AuthResponse {
    success: boolean;
    user?: User;
    error?: string;
}

class AuthService {
    private user: User | null = null;

    constructor() {
        // No localStorage initialization - sessions are server-side
    }

    /**
     * Login with email and password via backend API
     * Session cookie is set automatically by server
     */
    async login(email: string, password: string): Promise<AuthResponse> {
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Important! Allows cookies
                body: JSON.stringify({ email, password }),
            });

            const result = await response.json();

            if (result.success && result.user) {
                this.user = result.user;
                console.log('✅ [Auth Service] Login successful, session created');
                return result;
            } else {
                return { success: false, error: result.error || 'Login failed' };
            }
        } catch (error) {
            return { success: false, error: 'Network error' };
        }
    }

    /**
     * Register new user via backend API
     */
    async register(email: string, password: string, name: string): Promise<AuthResponse> {
        try {
            const response = await fetch('/api/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password, name }),
            });

            const result = await response.json();
            return result;
        } catch (error) {
            return { success: false, error: 'Network error' };
        }
    }

    /**
     * Get current user profile via session API
     * Session cookie is sent automatically
     */
    async getCurrentUser(): Promise<User | null> {
        try {
            // On the client, always use relative paths. On the server, use an absolute URL.
            let url = '/api/auth/session';
            if (typeof window === 'undefined') {
                const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000';
                url = new URL('/api/auth/session', baseUrl).toString();
            }

            debug.log('[Auth Service] Fetching user from session...', url);
            const response = await fetch(url, {
                credentials: 'include', // Important! Sends session cookie
                cache: 'no-store', // Prevent browser caching of the session state
            });

            debug.log('[Auth Service] Session response status:', response.status);
            const result = await response.json();

            debug.log('[Auth Service] Session result:', result);

            if (result.isAuthenticated && result.user) {
                debug.log('[Auth Service] User fetched from session:', result.user.email);
                this.user = result.user;
                return this.user;
            } else {
                debug.log('[Auth Service] No active session');
                this.user = null;
                return null;
            }
        } catch (error) {
            // Log with debug rather than console.error to avoid Next.js Turbopack dev error overlay on network aborts/drops
            debug.log('[Auth Service] Network error fetching session:', error);
            throw error;
        }
    }



    /**
     * Logout and destroy session
     */
    async logout(): Promise<void> {
        try {
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include',
            });
            console.log('✅ [Auth Service] Logout successful');
        } catch (error) {
            console.error('[Auth Service] Logout error:', error);
        }

        this.user = null;
    }

    /**
     * Check if user is authenticated
     * Note: This only checks cached user, call getCurrentUser() for fresh check
     */
    isAuthenticated(): boolean {
        return this.user !== null;
    }

    /**
     * Get cached user (may be stale)
     * Call getCurrentUser() for fresh data from session
     */
    getUser(): User | null {
        return this.user;
    }
}

// Export singleton instance
export const authService = new AuthService();

// Note: Legacy helper exports removed to enforce proper usage patterns.
// Use authService.method() directly or the React hooks from '@/hooks/useAuth'